// Copper oxidation matrix (MB.1). PURE (ENG-1): the block-name state machine a
// copper block walks as it weathers, and Minecraft's random-tick weathering
// probability. Four oxidation stages (unaffected → exposed → weathered →
// oxidized) climb the ladder over time; a honeycomb *waxes* a block (freezing
// its stage), and an axe either un-waxes it or *scrapes* one oxidation stage
// back off. Every transform here is a pure name→name map; the host applies the
// resulting block state. Rendering/interaction breadth (cut stairs/slab/door/
// trapdoor/bulb/grate/chiseled, waxed shapes) is deferred to MB.1-remainder.

/**
 * Each copper *form*'s four-stage oxidation ladder, unaffected → oxidized. The
 * word "copper" migrates within the name (`copper_block`/`exposed_copper` vs.
 * `cut_copper`/`exposed_cut_copper`), so each ladder is spelled out rather than
 * derived by prefixing. A `waxed_` copy of every entry exists at runtime.
 */
export const COPPER_LADDERS: readonly (readonly [string, string, string, string])[] = [
  ['copper_block', 'exposed_copper', 'weathered_copper', 'oxidized_copper'],
  ['cut_copper', 'exposed_cut_copper', 'weathered_cut_copper', 'oxidized_cut_copper'],
  // Full-cube shape forms (MB.1-remainder): each weathers within its own form, so
  // a chiseled_copper oxidizes to exposed_chiseled_copper — the shape is preserved.
  ['chiseled_copper', 'exposed_chiseled_copper', 'weathered_chiseled_copper', 'oxidized_chiseled_copper'],
  ['copper_grate', 'exposed_copper_grate', 'weathered_copper_grate', 'oxidized_copper_grate'],
  ['copper_bulb', 'exposed_copper_bulb', 'weathered_copper_bulb', 'oxidized_copper_bulb'],
  // Openables (MB.1): a copper door / trapdoor weathers within its own form,
  // preserving its facing/open/half props via the prop-preserving transform.
  ['copper_door', 'exposed_copper_door', 'weathered_copper_door', 'oxidized_copper_door'],
  ['copper_trapdoor', 'exposed_copper_trapdoor', 'weathered_copper_trapdoor', 'oxidized_copper_trapdoor'],
];

/** Oxidation stage names, ascending; index === stage. */
export const OXIDATION_STAGES = ['unaffected', 'exposed', 'weathered', 'oxidized'] as const;
export const OXIDIZED_STAGE = OXIDATION_STAGES.length - 1; // 3 — terminal

export interface CopperInfo {
  /** Index into `COPPER_LADDERS` (which form). */
  readonly ladder: number;
  /** Oxidation stage 0…3 (0 = unaffected, 3 = oxidized). */
  readonly stage: number;
  /** Whether the block is waxed (frozen; never oxidizes). */
  readonly waxed: boolean;
}

// Reverse index: every copper block name (waxed and un-waxed) → its coordinates
// in the matrix. Built once; the ladders are the single source of truth.
const INDEX = new Map<string, CopperInfo>();
for (let li = 0; li < COPPER_LADDERS.length; li++) {
  for (let s = 0; s < COPPER_LADDERS[li]!.length; s++) {
    const n = COPPER_LADDERS[li]![s]!;
    INDEX.set(n, { ladder: li, stage: s, waxed: false });
    INDEX.set(`waxed_${n}`, { ladder: li, stage: s, waxed: true });
  }
}

/** Every copper block name (16 = 2 forms × 4 stages × waxed/un-waxed). */
export const COPPER_BLOCK_NAMES: readonly string[] = [...INDEX.keys()];

/** Resolve a block name to its matrix coordinates, or null if it isn't copper. */
export function copperInfo(name: string | null | undefined): CopperInfo | null {
  return name != null ? INDEX.get(name) ?? null : null;
}
export function isCopper(name: string | null | undefined): boolean {
  return copperInfo(name) !== null;
}
/** Oxidation stage 0…3, or -1 if the name isn't a copper block. */
export function oxidationStageOf(name: string | null | undefined): number {
  return copperInfo(name)?.stage ?? -1;
}
export function isWaxed(name: string | null | undefined): boolean {
  return copperInfo(name)?.waxed ?? false;
}
/** A block can still oxidize iff it's copper, un-waxed, and below oxidized. */
export function canOxidize(name: string | null | undefined): boolean {
  const i = copperInfo(name);
  return i !== null && !i.waxed && i.stage < OXIDIZED_STAGE;
}

const nameAt = (i: CopperInfo, stage: number): string => {
  const base = COPPER_LADDERS[i.ladder]![stage]!;
  return i.waxed ? `waxed_${base}` : base;
};

/** Advance one oxidation stage (random-tick result). Null if waxed or already
 *  oxidized — i.e. exactly when {@link canOxidize} is false. */
export function weatherStep(name: string): string | null {
  const i = copperInfo(name);
  if (i === null || i.waxed || i.stage >= OXIDIZED_STAGE) return null;
  return nameAt(i, i.stage + 1);
}

/** De-oxidize one stage (axe scrape). Preserves the waxed flag. Null at
 *  stage 0 (nothing to scrape) or on a non-copper block. */
export function scrapeStep(name: string): string | null {
  const i = copperInfo(name);
  if (i === null || i.stage <= 0) return null;
  return nameAt(i, i.stage - 1);
}

/** Wax a block (honeycomb). Null if already waxed or not copper. */
export function waxStep(name: string): string | null {
  const i = copperInfo(name);
  if (i === null || i.waxed) return null;
  return `waxed_${COPPER_LADDERS[i.ladder]![i.stage]!}`;
}

/** Un-wax a block (axe). Null if not waxed or not copper. */
export function unwaxStep(name: string): string | null {
  const i = copperInfo(name);
  if (i === null || !i.waxed) return null;
  return COPPER_LADDERS[i.ladder]![i.stage]!;
}

/**
 * Right-clicking copper with an axe: Minecraft's precedence is un-wax first (a
 * waxed block scrapes back to the same-stage un-waxed block), otherwise scrape
 * one oxidation stage off. Returns the new name and which happened, or null
 * (un-waxed stage-0 copper — nothing for the axe to do).
 */
export function axeStep(name: string): { result: string; kind: 'unwax' | 'scrape' } | null {
  const un = unwaxStep(name);
  if (un !== null) return { result: un, kind: 'unwax' };
  const sc = scrapeStep(name);
  if (sc !== null) return { result: sc, kind: 'scrape' };
  return null;
}

// ── Random-tick weathering probability (Minecraft's exact model) ──
//
// Every random tick a copper block passes an outer gate, then rolls a
// neighbourhood-weighted chance. The scan (host-side) walks every block within
// taxicab distance 4 and classifies each *other* copper block by oxidation
// relative to self. If any neighbour is *less* oxidized, weathering is vetoed
// entirely; otherwise the chance rises with the share of equally/more-oxidized
// copper around it (so a lone block weathers slowly, a wall of copper fast).

/** Outer per-random-tick gate (Minecraft's `WeatheringCopper` 0.05688889). */
export const OUTER_TICK_CHANCE = 0.05688889;

export interface OxidationNeighbourhood {
  /** Any copper neighbour (taxicab ≤ 4) strictly *less* oxidized than self. */
  readonly hasLesserNeighbour: boolean;
  /** Copper neighbours strictly *more* oxidized than self (Minecraft's `k`). */
  readonly greaterCount: number;
  /** Copper neighbours at the *same* oxidation as self (Minecraft's `j`). */
  readonly sameCount: number;
}

/**
 * Minecraft's weathering probability `g` for a block at `selfStage` given its
 * copper neighbourhood. A less-oxidized neighbour vetoes weathering (→ 0);
 * oxidized copper is terminal (→ 0). Otherwise `f = (k+1)/(k+j+1)` and
 * `g = f² · mod`, where `mod` is 0.75 while unaffected, else 1.
 */
export function oxidationChance(selfStage: number, nb: OxidationNeighbourhood): number {
  if (selfStage < 0 || selfStage >= OXIDIZED_STAGE) return 0;
  if (nb.hasLesserNeighbour) return 0;
  const k = nb.greaterCount;
  const j = nb.sameCount;
  const f = (k + 1) / (k + j + 1);
  const mod = selfStage === 0 ? 0.75 : 1.0;
  return f * f * mod;
}

/**
 * Full random-tick decision: the outer gate (`outerRoll`) times the
 * neighbourhood chance (`chanceRoll`). Both rolls are uniform 0…1.
 */
export function shouldOxidize(
  outerRoll: number,
  chanceRoll: number,
  selfStage: number,
  nb: OxidationNeighbourhood,
): boolean {
  if (outerRoll >= OUTER_TICK_CHANCE) return false;
  return chanceRoll < oxidationChance(selfStage, nb);
}
